/**
 * API client for a dealer's morning inventory-audit alerts (revenue-assurance
 * plan, Phase 2). Mirrors the backend schemas in backend/schemas.py
 * (AuditAlert*) and the /audit-alerts router. The server resolves the dealer
 * group; a dealer user never has to send one.
 */
import { API } from "../config/api"

const TOKEN_KEY = "invi_token"

export interface AuditAlertProblem {
  slug: string
}

/** One row of the Alerts page's checkbox list. */
export interface AuditAlertProblemOption {
  slug: string
  label: string
  action: string
  severity: string
}

export interface AuditAlert {
  id: number
  name: string
  recipients: string[]
  problems: AuditAlertProblem[]
  hour: number
  minute: number
  is_enabled: boolean
  dealer_group_id: number | null
  last_sent_at: string | null
  created_at: string
  /** One of the group's stores the email is narrowed to, or null for the whole group. */
  rooftop_slug: string | null
  /** That store's short name as the audit rows carry it ("Fernley CDJR"). */
  rooftop_label: string | null
}

export interface AuditAlertInput {
  name: string
  recipients: string[]
  problems: AuditAlertProblem[]
  hour: number
  minute: number
  is_enabled?: boolean
  /** A store's slug to narrow the email to; null means the whole group. */
  rooftop_slug: string | null
  /** Operators on "All Groups" must name one; dealer users leave it out. */
  dealer_group_id?: number | null
}

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem(TOKEN_KEY)
  return token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const data = await res.json().catch(() => null)
    throw new Error(data?.detail || `Request failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

export async function fetchAlertProblems(): Promise<AuditAlertProblemOption[]> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.auditAlerts.problems}`, {
    headers: authHeaders(),
  })
  return handleResponse<AuditAlertProblemOption[]>(res)
}

export async function fetchAuditAlerts(dealerGroupId: number | null): Promise<AuditAlert[]> {
  const query = dealerGroupId === null ? "" : `?dealer_group_id=${dealerGroupId}`
  const res = await fetch(`${API.baseUrl}${API.endpoints.auditAlerts.base}${query}`, {
    headers: authHeaders(),
  })
  return handleResponse<AuditAlert[]>(res)
}

export async function createAuditAlert(input: AuditAlertInput): Promise<AuditAlert> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.auditAlerts.base}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(input),
  })
  return handleResponse<AuditAlert>(res)
}

export async function updateAuditAlert(
  id: number,
  patch: Partial<AuditAlertInput>,
): Promise<AuditAlert> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.auditAlerts.byId(id)}`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify(patch),
  })
  return handleResponse<AuditAlert>(res)
}

export async function deleteAuditAlert(id: number): Promise<void> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.auditAlerts.byId(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  })
  await handleResponse<{ status: string }>(res)
}

/**
 * Sends the alert right now. Resolves to whether an email went out: with no
 * audit run newer than the last email there is nothing fresh to send, and the
 * page says so instead of claiming it sent something.
 */
export async function sendAuditAlertNow(id: number): Promise<boolean> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.auditAlerts.sendNow(id)}`, {
    method: "POST",
    headers: authHeaders(),
  })
  const data = await handleResponse<{ status: string; sent: boolean }>(res)
  return data.sent
}
